// file: `react/src/components/ChatscopeThread/ChatscopeThread.tsx`:

import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  MessageSeparator,
  ConversationHeader,
  TypingIndicator,
  Overlay,
  InputToolbox,
  InfoButton,
  Avatar
} from '@chatscope/chat-ui-kit-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/free-solid-svg-icons';
import { useUser } from '../UserContext.tsx';
import type { ConnectionStatus } from '../ChatSocketContext.tsx';
import { chatscopeAvatarSrc } from '../chatscopeAvatar.ts';
import { memberAvatarNode } from '../MemberAvatars.tsx';
import { isSelfMessage, isMyReaction, reactionKey } from '../../utils/messageListUtils.ts';
import { roomSecurityLabel, ROOM_TYPES } from '../threadRoomType.ts';
import PasswordUnlock from '../PasswordUnlock.tsx';
import { isPupil } from '../../school/schoolClaims.ts';
import { mascotSuggestion } from '../../school/mascotCoach.ts';
import { requestSchoolHelp } from '../../school/schoolApi.ts';
import ThreadPhoto from '../ThreadPhoto.tsx';
import './ChatscopeThread.scss';

export const REACTION_EMOJIS = ['❤️', '🖖', '👍', '👎', '😂', '😮', '😢', '🎉'];
export const COMPOSER_EMOJIS = ['😊', '❤️', '👍', '😂', '🎉', '🙏', '👋', '🔥'];

function reactionsOf(msg) {
  const sender = msg.sender || '';
  if (Array.isArray(msg.reactions) && msg.reactions.length > 0) {
    return msg.reactions
      .filter((r) => r && r.emoji && r.emoji !== '-')
      .map((r) => ({ ...r, user: r.user || sender }))
      .filter((r) => r.user);
  }
  if (msg.emoji && msg.emoji !== '-' && sender) return [{ user: sender, emoji: msg.emoji }];
  return [];
}

function formatTime(time) {
  if (!time) return '';
  const d = new Date(time);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDay(time) {
  if (!time) return '';
  const d = new Date(time);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function sameDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return da.toDateString() === db.toDateString();
}

function messagePosition(messages, index) {
  const msg = messages[index];
  const prev = messages[index - 1];
  const next = messages[index + 1];
  const samePrev = prev && prev.sender === msg.sender && sameDay(prev.time, msg.time);
  const sameNext = next && next.sender === msg.sender && sameDay(next.time, msg.time);
  if (!samePrev && !sameNext) return 'single';
  if (!samePrev && sameNext) return 'first';
  if (samePrev && sameNext) return 'normal';
  return 'last';
}

function headerInfo(thread, encrypted, encryptionLocked, online) {
  const members = thread?.members || [];
  const names = members.map((m) => m.shortName).filter(Boolean);
  const present = names.filter((name) => online.includes(name));
  const encryptionEnabled = !!thread?.encryptionEnabled || encrypted;
  const bits = [];
  if (names.length) bits.push(names.join(', '));
  if (thread) {
    bits.push(roomSecurityLabel(
      { roomType: thread.roomType, encryptionEnabled },
      encryptionLocked
    ));
  }
  if (!encryptionEnabled && present.length === 1) bits.push(`${present[0]} is here`);
  else if (!encryptionEnabled && present.length > 1) bits.push(`${present.length} online`);
  return bits.join(' · ');
}

function connectionStatusLabel(status: ConnectionStatus): string {
  if (status === 'connecting') return 'Connecting…';
  if (status === 'offline') return 'Live updates paused — sending may fail';
  return '';
}

function ChatscopeThread({
  data,
  thread,
  threadId,
  handleDeleteMessage,
  handleReact,
  onSend,
  encryptionLocked = false,
  encrypted = false,
  threadKey = null,
  online = [],
  connectionStatus = 'online',
  typingNames = [],
  onBack,
  detailsOpen,
  onToggleDetails,
  threadQuery = '',
  replyTo,
  onReply,
  draft,
  onDraft,
  attachmentsDisabled = false,
  onAttach,
  onTyping,
  onOpenImage,
  sendDisabled,
  sending = false,
  sendError,
  onRetrySend,
  historyError,
  onRetryHistory,
  actionError,
  onDismissActionError
}) {
  const { users, activeUser } = useUser();
  const [menuId, setMenuId] = useState(null);
  const [mascot, setMascot] = useState<{
    kind: string;
    prompt?: string;
    suggestion?: string;
    original: string;
  } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpStatus, setHelpStatus] = useState('');
  const [helpBusy, setHelpBusy] = useState(false);
  const longPressTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageListRef = useRef(null);
  const pendingBottomRef = useRef(threadId);
  const paneRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (e.target.closest('.emoji-badge, .context-menu, .cs-message, .chatscope-quote-actions')) return;
      setMenuId(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    pendingBottomRef.current = threadId;
  }, [threadId]);

  useEffect(() => {
    setMenuId(null);
    setMascot(null);
    setHelpOpen(false);
    setHelpStatus('');
    setHelpBusy(false);
  }, [threadId]);

  useEffect(() => {
    if (data == null) return;
    if (pendingBottomRef.current !== threadId) return;

    const scrollToLatest = () => messageListRef.current?.scrollToBottom?.('auto');
    let cancelled = false;
    let nestedFrame = 0;
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      scrollToLatest();
      nestedFrame = requestAnimationFrame(() => {
        if (cancelled) return;
        scrollToLatest();
        pendingBottomRef.current = null;
      });
    });
    const timeouts = [50, 200].map((ms) => window.setTimeout(scrollToLatest, ms));

    const root = paneRef.current;
    const images = root ? Array.from(root.querySelectorAll('img')) : [];
    images.forEach((img) => {
      if (!img.complete) img.addEventListener('load', scrollToLatest);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(nestedFrame);
      timeouts.forEach((id) => window.clearTimeout(id));
      images.forEach((img) => img.removeEventListener('load', scrollToLatest));
    };
  }, [threadId, data]);

  const storeReaction = (msg, picked) => {
    if (handleReact) handleReact(msg, picked);
    setMenuId(null);
  };

  const allMessages = Array.isArray(data) ? data : [];
  const q = (threadQuery || '').trim().toLowerCase();
  const messages = q
    ? allMessages.filter((msg) => {
      const blob = `${msg.sender || ''} ${msg.text || ''} ${msg.replyTo?.text || ''}`.toLowerCase();
      return blob.includes(q);
    })
    : allMessages;

  const typingLine = (() => {
    const others = (typingNames || []).filter((name) => name && name !== activeUser?.shortName);
    if (!others.length) return '';
    if (others.length === 1) return `${others[0]} is typing`;
    return `${others.join(', ')} are typing`;
  })();

  const showConnectionBanner = connectionStatus !== 'online';
  const classmateThread = thread?.roomType === ROOM_TYPES.SCHOOL_CLASSMATE;
  const composerPlaceholder = encryptionLocked
    ? 'Enter login password to send'
    : sending
      ? 'Sending…'
      : 'Type a message';

  const handleComposerSend = (textContent) => {
    if (classmateThread) {
      const coach = mascotSuggestion(textContent);
      if (coach.kind === 'rewrite' && mascot?.original !== textContent) {
        setMascot({ ...coach, original: textContent });
        return;
      }
    }
    setMascot(null);
    if (onSend) onSend(textContent);
  };

  const sendHelp = async (audience: 'class-teacher' | 'safeguarding-lead' | 'parent') => {
    setHelpBusy(true);
    setHelpStatus('');
    try {
      await requestSchoolHelp(audience, threadId);
      setHelpStatus('Asked for help. The classmate thread was not copied.');
      setHelpOpen(false);
    } catch (cause) {
      setHelpStatus(cause instanceof Error ? cause.message : 'Could not send that help request');
    } finally {
      setHelpBusy(false);
    }
  };

  return (
    <div className="chatscope-thread-root" ref={paneRef}>
      <ChatContainer>
        <ConversationHeader>
          <ConversationHeader.Back onClick={onBack} />
          {memberAvatarNode({
            members: thread?.members,
            online,
            size: 'md',
            fallback: (thread?.name || '?').trim().charAt(0)
          })}
          <ConversationHeader.Content
            userName={thread?.name || `Conversation ${threadId}`}
            info={headerInfo(thread, encrypted, encryptionLocked, online)}
          />
          <ConversationHeader.Actions>
            {classmateThread && isPupil(activeUser) && (
              <button
                type="button"
                className="chatscope-help-btn"
                onClick={() => setHelpOpen((open) => !open)}
              >
                I need help
              </button>
            )}
            <InfoButton
              title={detailsOpen ? 'Hide details' : 'Conversation details'}
              onClick={onToggleDetails}
              className={detailsOpen ? 'is-open' : ''}
            />
          </ConversationHeader.Actions>
        </ConversationHeader>
        {showConnectionBanner && (
          <div
            className={`chatscope-connection-status chatscope-connection-status--${connectionStatus}`}
            role="status"
          >
            {connectionStatusLabel(connectionStatus)}
          </div>
        )}
        <MessageList
          ref={messageListRef}
          loading={data == null && !historyError}
          autoScrollToBottom
          autoScrollToBottomOnMount
          scrollBehavior="auto"
          typingIndicator={typingLine ? <TypingIndicator content={typingLine} /> : undefined}
        >
          {historyError && (
            <MessageSeparator>
              <div className="chatscope-thread-error">
                <span>{historyError}</span>
                {onRetryHistory && (
                  <button type="button" className="chatscope-thread-error__retry" onClick={onRetryHistory}>
                    Retry
                  </button>
                )}
              </div>
            </MessageSeparator>
          )}
          {q && (
            <MessageSeparator>{messages.length} match{messages.length === 1 ? '' : 'es'}</MessageSeparator>
          )}
          {messages.flatMap((msg, index) => {
            const self = isSelfMessage(msg, activeUser?.id, activeUser?.shortName);
            const direction = self ? 'outgoing' : 'incoming';
            const position = messagePosition(messages, index);
            const user = users.find((u) => u.shortName === msg.sender);
            const emoji = user?.emoji || '';
            const initial = user?.shortName?.[0] || msg.sender?.[0] || '?';
            const reactions = reactionsOf(msg);
            const myReaction = reactions.find((r) => isMyReaction(r, activeUser?.id, activeUser?.shortName))?.emoji;
            const showMenu = String(menuId) === String(msg.id);
            const showAvatar = !self && (position === 'single' || position === 'last');
            const hasImage = msg.type === 'image' && msg.mediaUrl;
            const quoted = msg.replyTo && (msg.replyTo.sender || msg.replyTo.text);
            const model: any = {
              direction,
              position,
              sender: msg.sender,
              sentTime: formatTime(msg.time),
              type: hasImage || quoted ? 'custom' : 'text',
              message: msg.text || ''
            };

            const showDay = index === 0 || !sameDay(messages[index - 1]?.time, msg.time);

            return [
              showDay && msg.time ? (
                <MessageSeparator key={`day-${msg.id}`}>{formatDay(msg.time)}</MessageSeparator>
              ) : null,
              <Message
                key={msg.id}
                model={model}
                avatarSpacer={!self && !showAvatar}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenuId(msg.id);
                }}
                onTouchStart={() => {
                  longPressTimeout.current = setTimeout(() => setMenuId(msg.id), 500);
                }}
                onTouchEnd={() => {
                  if (longPressTimeout.current) clearTimeout(longPressTimeout.current);
                }}
              >
                {showAvatar && (
                  <Avatar
                    src={chatscopeAvatarSrc(emoji || initial)}
                    name={msg.sender}
                    status={online.includes(msg.sender) ? 'available' : 'unavailable'}
                  />
                )}
                {(position === 'first' || position === 'single') && !self && (
                  <Message.Header sender={msg.sender} sentTime={formatTime(msg.time)} />
                )}
                {(hasImage || quoted) && (
                  <Message.CustomContent>
                    <div className="chatscope-custom-body">
                      {quoted && (
                        <div className="chatscope-quote">
                          <div className="chatscope-quote-sender">{msg.replyTo.sender}</div>
                          <div className="chatscope-quote-text">{msg.replyTo.text || 'Message'}</div>
                        </div>
                      )}
                      {msg.text ? <div className="chatscope-custom-text">{msg.text}</div> : null}
                      {hasImage && (
                        <ThreadPhoto
                          message={msg}
                          threadKey={threadKey}
                          encryptionLocked={encryptionLocked}
                          onOpen={onOpenImage}
                          alt="User submitted"
                        />
                      )}
                    </div>
                  </Message.CustomContent>
                )}
                <Message.Footer>
                  {showMenu ? (
                    <div className="emoji-badge-container">
                      <div className="emoji-badge" onClick={(e) => e.stopPropagation()}>
                        {REACTION_EMOJIS.map((item) => (
                          <Button
                            key={item}
                            className={`emoji${myReaction === item ? ' selected' : ''}`}
                            onClick={() => storeReaction(msg, item)}
                          >
                            {item}
                          </Button>
                        ))}
                        <button
                          type="button"
                          className="card context-menu-item"
                          onClick={() => {
                            setMenuId(null);
                            if (onReply) onReply(msg);
                          }}
                        >
                          <FontAwesomeIcon icon={faReply} /> Reply
                        </button>
                        {self && (
                          <button
                            type="button"
                            className="card context-menu-item"
                            onClick={() => {
                              setMenuId(null);
                              if (handleDeleteMessage) handleDeleteMessage(msg.id);
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ) : reactions.length > 0 ? (
                    <div className="emoji-badge-container">
                      {reactions.map((reaction) => (
                        <button
                          type="button"
                          key={reactionKey(reaction)}
                          className={`emoji-badge emoji-reaction${isMyReaction(reaction, activeUser?.id, activeUser?.shortName) ? ' mine' : ''}`}
                          title={reaction.user || undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            storeReaction(msg, reaction.emoji);
                          }}
                        >
                          {reaction.emoji}
                        </button>
                      ))}
                    </div>
                  ) : (position === 'last' || position === 'single') ? (
                    <span className="chatscope-time">{formatTime(msg.time)}</span>
                  ) : null}
                </Message.Footer>
              </Message>
            ];
          })}
        </MessageList>
        <MessageInput
          placeholder={composerPlaceholder}
          disabled={encryptionLocked || sending}
          attachButton
          attachDisabled={attachmentsDisabled || encryptionLocked || sending}
          sendDisabled={sendDisabled || encryptionLocked || sending || !String(draft || '').trim()}
          value={draft}
          autoFocus
          activateAfterChange
          onChange={(_html, textContent) => {
            if (onDraft) onDraft(textContent);
            if (textContent && onTyping) onTyping();
          }}
          onSend={(_html, textContent) => handleComposerSend(textContent)}
          onAttachClick={() => onAttach && onAttach()}
        />
        <InputToolbox>
          {classmateThread && mascot?.kind === 'rewrite' && (
            <div className="chatscope-mascot" role="status">
              <div>{mascot.prompt}</div>
              <div className="chatscope-mascot-actions">
                {mascot.suggestion && (
                  <button
                    type="button"
                    onClick={() => {
                      if (onDraft) onDraft(mascot.suggestion || '');
                      setMascot(null);
                    }}
                  >
                    Use suggestion
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleComposerSend(mascot.original)}
                >
                  Send anyway
                </button>
                <button type="button" onClick={() => setMascot(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {classmateThread && helpOpen && (
            <div className="chatscope-help" role="dialog" aria-label="I need help">
              <p>Choose who to tell. This does not copy classmate chat.</p>
              <div className="chatscope-mascot-actions">
                <button type="button" disabled={helpBusy} onClick={() => sendHelp('class-teacher')}>
                  Class teacher
                </button>
                <button type="button" disabled={helpBusy} onClick={() => sendHelp('safeguarding-lead')}>
                  Safeguarding lead
                </button>
                <button type="button" disabled={helpBusy} onClick={() => sendHelp('parent')}>
                  A parent
                </button>
              </div>
            </div>
          )}
          {helpStatus && (
            <div className="chatscope-action-error" role="status">{helpStatus}</div>
          )}
          {actionError && (
            <div className="chatscope-action-error" role="alert">
              <span>{actionError}</span>
              {onDismissActionError && (
                <button type="button" className="chatscope-action-error__dismiss" onClick={onDismissActionError}>
                  Dismiss
                </button>
              )}
            </div>
          )}
          {sendError && (
            <div className="chatscope-send-error" role="alert">
              <span>{sendError}</span>
              {onRetrySend && (
                <button type="button" className="chatscope-send-error__retry" onClick={onRetrySend}>
                  Retry
                </button>
              )}
            </div>
          )}
          {replyTo && (
            <div className="chatscope-reply-bar">
              <div>
                <strong>Replying to {replyTo.sender}</strong>
                <div className="chatscope-reply-preview">{replyTo.text || 'Message'}</div>
              </div>
              <button type="button" className="chatscope-reply-cancel" onClick={() => onReply && onReply(null)}>
                Cancel
              </button>
            </div>
          )}
          <div className="chatscope-composer-emojis">
            {COMPOSER_EMOJIS.map((item) => (
              <button
                type="button"
                key={item}
                className="emoji"
                disabled={encryptionLocked || sending}
                onClick={() => onDraft && onDraft(`${draft || ''}${item}`)}
              >
                {item}
              </button>
            ))}
          </div>
        </InputToolbox>
      </ChatContainer>
      {encryptionLocked && (
        <Overlay blur className="chatscope-lock-overlay">
          <PasswordUnlock />
        </Overlay>
      )}
    </div>
  );
}

export default ChatscopeThread;
